import { z } from "zod";

/**
 * The API client.
 *
 * Deliberately free of any Expo or React Native import. Reaching for
 * `expo-constants` here would drag React Native's Flow-typed entry point into
 * anything that imports this file, including the test runner, which cannot
 * parse Flow. Host detection lives in `api-host.ts`; this module is told the
 * answer.
 *
 * Responses are validated against the shared contract rather than trusted.
 * The API and this app are versioned together today, but they will not always
 * be -- a phone keeps whatever build the user last installed, so "the server
 * and client always agree" stops being true the first time somebody declines
 * an update.
 */

let baseUrl = process.env["EXPO_PUBLIC_API_URL"]?.replace(/\/$/, "") ?? "http://127.0.0.1:8787";

export function setApiBaseUrl(url: string): void {
  baseUrl = url.replace(/\/$/, "");
}

export function apiBaseUrl(): string {
  return baseUrl;
}

/**
 * How the client gets a session token.
 *
 * A function rather than a token, because Clerk's tokens are short-lived and
 * refreshed behind the scenes -- storing one would work for about a minute and
 * then start producing 401s that look like being signed out.
 *
 * Left unset outside the app (tests, tooling), where requests are anonymous.
 */
type TokenProvider = () => Promise<string | null>;

let tokenProvider: TokenProvider | undefined;

export function setTokenProvider(provider: TokenProvider | undefined): void {
  tokenProvider = provider;
}

/** The subset of a game the list and detail screens use. */
export const apiGameSchema = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  /** Present on list rows too, so a result can show a cover. */
  imageUrl: z.url().nullable().optional(),
  yearPublished: z.number().nullable().optional(),
  designers: z.array(z.string()).default([]),
  minPlayers: z.number().nullable().optional(),
  maxPlayers: z.number().nullable().optional(),
  playingTimeMinutes: z.number().nullable().optional(),
});
export type ApiGame = z.infer<typeof apiGameSchema>;

const searchResponseSchema = z.object({
  query: z.string(),
  count: z.number(),
  results: z.array(apiGameSchema),
});

/**
 * A credit the page is obliged to render (WI-072, ADR-0016).
 *
 * CC BY-SA material must name its source and licence wherever it appears.
 * The API sends these with the game rather than behind a second request,
 * because a client that has to ask again can render the material before the
 * credit arrives -- or forget to ask.
 */
export const creditSchema = z.object({
  source: z.string(),
  licence: z.string(),
  url: z.url(),
  /** Named where the licence requires it. */
  author: z.string().optional(),
  fields: z.array(z.string()),
});
export type Credit = z.infer<typeof creditSchema>;

const gameDetailSchema = apiGameSchema.extend({
  description: z.string().nullable().optional(),
  publishers: z.array(z.string()).default([]),
  mechanics: z.array(z.string()).default([]),
  themes: z.array(z.string()).default([]),
  weight: z.string().nullable().optional(),
  /**
   * A picture of the game, when one exists whose licence we could read.
   *
   * Never rendered without the matching credit in `attribution` -- that is
   * what CC BY-SA obliges, and it is why the two travel together.
   */
  imageUrl: z.url().nullable().optional(),
  // Defaulted: an older API that does not send them yields no credits rather
  // than failing to parse the whole game.
  attribution: z.array(creditSchema).default([]),
});
export type ApiGameDetail = z.infer<typeof gameDetailSchema>;

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface RequestOptions {
  signal?: AbortSignal;
  authenticated?: boolean;
  method?: "GET" | "PUT" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
}

/**
 * Everything up to the body: auth, transport failures, HTTP status.
 *
 * Split out from `request` because a 204 has no body to parse, and calling
 * `.json()` on one throws -- which would turn a successful delete into an
 * error about malformed JSON.
 */
async function send(path: string, options: RequestOptions): Promise<Response> {
  const { signal, authenticated = false, method = "GET", body } = options;
  const headers: Record<string, string> = {};

  if (body !== undefined) headers["content-type"] = "application/json";

  if (authenticated) {
    const token = await tokenProvider?.();
    if (!token) {
      // Asking the API to reject us would work, but it costs a round trip to
      // learn something already known here.
      throw new ApiError("You need to be signed in.", 401);
    }
    headers["authorization"] = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(`${baseUrl}${path}`, {
      signal,
      headers,
      method,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch (cause) {
    if ((cause as Error)?.name === "AbortError") throw cause;
    // A dev server that is not running is the most likely failure by far, and
    // "Network request failed" is not a useful thing to show a person.
    throw new ApiError(`Could not reach the API at ${baseUrl}. Is it running?`);
  }

  if (response.status === 401) {
    // Distinguishable, because a screen should send somebody to sign in rather
    // than show them "the API returned 401".
    throw new ApiError("Your session has expired. Sign in again.", 401);
  }

  if (!response.ok) {
    throw new ApiError(`The API returned ${response.status}.`, response.status);
  }

  return response;
}

async function request<T>(
  path: string,
  schema: z.ZodType<T>,
  options: RequestOptions = {},
): Promise<T> {
  const response = await send(path, options);

  const parsed = schema.safeParse(await response.json());
  if (!parsed.success) {
    // Failing loudly beats rendering undefined into the UI.
    throw new ApiError(`The API returned something unexpected: ${parsed.error.issues[0]?.message}`);
  }
  return parsed.data;
}

/** For the endpoints that answer 204, which have nothing to validate. */
async function requestNoContent(path: string, options: RequestOptions = {}): Promise<void> {
  await send(path, options);
}

export async function searchGames(
  query: string,
  options: { limit?: number; signal?: AbortSignal } = {},
): Promise<ApiGame[]> {
  const q = query.trim();
  // The API rejects a blank query with a 400. Not asking is better than
  // showing the user an error they did not cause.
  if (q.length === 0) return [];

  const params = new URLSearchParams({ q, limit: String(options.limit ?? 20) });
  const body = await request(`/games/search?${params}`, searchResponseSchema, {
    signal: options.signal,
  });
  return body.results;
}

export function fetchGame(slug: string, signal?: AbortSignal): Promise<ApiGameDetail> {
  return request(`/games/${encodeURIComponent(slug)}`, gameDetailSchema, { signal });
}

/**
 * Who the API thinks we are.
 *
 * The first call creates the local user row (ADR-0005), so this is also what
 * brings a Clerk account into our database. It returns OUR id -- a client
 * passing Clerk's id around has misunderstood the model.
 */
export const meSchema = z.object({
  id: z.string(),
  displayName: z.string(),
});
export type Me = z.infer<typeof meSchema>;

export function renameMe(displayName: string): Promise<Me> {
  return request("/me", meSchema, {
    method: "PATCH",
    body: { displayName },
    authenticated: true,
  });
}

export function fetchMe(signal?: AbortSignal): Promise<Me> {
  return request("/me", meSchema, { signal, authenticated: true });
}

// ---------------------------------------------------------------------------
// shelf (WI-031)
// ---------------------------------------------------------------------------

/**
 * The three shelf states.
 *
 * Restated here rather than imported: `packages/db` is the ORM and must never
 * reach a client bundle (ADR-0022), and the generated contract is derived from
 * Drizzle columns, which cannot express an enum over a `text` column. The
 * equivalence is asserted in a test instead.
 */
export const SHELF_STATUSES = ["owned", "wishlist", "none"] as const;
export type ShelfStatus = (typeof SHELF_STATUSES)[number];

export const shelfEntrySchema = z.object({
  slug: z.string(),
  status: z.enum(SHELF_STATUSES),
  /** Projected from sessions; never written from here (ADR-0021). */
  rating: z.number().nullable().optional(),
  review: z.string().nullable().optional(),
});
export type ShelfEntry = z.infer<typeof shelfEntrySchema>;

export const shelfItemSchema = shelfEntrySchema.omit({ slug: true }).extend({
  gameId: z.string(),
  slug: z.string(),
  title: z.string(),
  imageUrl: z.url().nullable().optional(),
  yearPublished: z.number().nullable().optional(),
  designers: z.array(z.string()).default([]),
});
export type ShelfItem = z.infer<typeof shelfItemSchema>;

const shelfResponseSchema = z.object({
  count: z.number(),
  items: z.array(shelfItemSchema),
});

export async function fetchShelf(
  options: { status?: ShelfStatus; signal?: AbortSignal } = {},
): Promise<ShelfItem[]> {
  const query = options.status ? `?status=${options.status}` : "";
  const body = await request(`/me/shelf${query}`, shelfResponseSchema, {
    signal: options.signal,
    authenticated: true,
  });
  return body.items;
}

export function fetchShelfEntry(slug: string, signal?: AbortSignal): Promise<ShelfEntry> {
  return request(`/me/shelf/${encodeURIComponent(slug)}`, shelfEntrySchema, {
    signal,
    authenticated: true,
  });
}

export function setShelfStatus(slug: string, status: ShelfStatus): Promise<ShelfEntry> {
  return request(`/me/shelf/${encodeURIComponent(slug)}`, shelfEntrySchema, {
    method: "PUT",
    body: { status },
    authenticated: true,
  });
}

// ---------------------------------------------------------------------------
// sessions and players (WI-036)
// ---------------------------------------------------------------------------

export const playerSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  /** Null for almost everyone: a player is not a user (ADR-0006). */
  userId: z.string().nullable().optional(),
});
export type Player = z.infer<typeof playerSchema>;

const playersResponseSchema = z.object({ count: z.number(), items: z.array(playerSchema) });

export async function fetchPlayers(signal?: AbortSignal): Promise<Player[]> {
  const body = await request("/me/players", playersResponseSchema, {
    signal,
    authenticated: true,
  });
  return body.items;
}

export function createPlayer(displayName: string): Promise<Player> {
  return request("/me/players", playerSchema, {
    method: "POST",
    body: { displayName },
    authenticated: true,
  });
}

/**
 * What a logged play can carry.
 *
 * Every field is optional, which is ADR-0007 rather than laziness: one tap is
 * a complete record and the form must be able to send nothing at all.
 */
export interface NewPlay {
  /** ISO 8601. Absent means the API decides, which means now. */
  playedAt?: string;
  rating?: number | null;
  notes?: string | null;
  durationMinutes?: number | null;
}

const loggedSessionSchema = z.object({
  id: z.string(),
  slug: z.string(),
  shelf: shelfEntrySchema.omit({ slug: true }).nullable().optional(),
});
export type LoggedSession = z.infer<typeof loggedSessionSchema>;

export function logPlay(slug: string, play: NewPlay = {}): Promise<LoggedSession> {
  return request(`/games/${encodeURIComponent(slug)}/sessions`, loggedSessionSchema, {
    method: "POST",
    body: play,
    authenticated: true,
  });
}

export const participantSchema = z.object({
  playerId: z.string(),
  displayName: z.string(),
  isWinner: z.boolean().nullable().optional(),
  score: z.string().nullable().optional(),
});
export type Participant = z.infer<typeof participantSchema>;

const participantsResponseSchema = z.object({
  count: z.number(),
  participants: z.array(participantSchema),
});

export async function setParticipants(
  sessionId: string,
  participants: { playerId: string; isWinner?: boolean | null }[],
): Promise<Participant[]> {
  const body = await request(
    `/me/sessions/${encodeURIComponent(sessionId)}/participants`,
    participantsResponseSchema,
    { method: "PUT", body: { participants }, authenticated: true },
  );
  return body.participants;
}

export const sessionSchema = z.object({
  id: z.string(),
  gameId: z.string(),
  slug: z.string(),
  title: z.string(),
  playedAt: z.string(),
  rating: z.number().nullable().optional(),
  review: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  tags: z.array(z.string()).default([]),
  /**
   * Who was at the table, carried on the session rather than fetched per row
   * (WI-043). Defaulted, so a client newer than the API degrades to "nobody
   * recorded" instead of failing to parse the whole list.
   */
  participants: z.array(participantSchema).default([]),
});
export type Session = z.infer<typeof sessionSchema>;

const sessionsResponseSchema = z.object({ count: z.number(), items: z.array(sessionSchema) });

export async function fetchSessions(signal?: AbortSignal): Promise<Session[]> {
  const body = await request("/me/sessions", sessionsResponseSchema, {
    signal,
    authenticated: true,
  });
  return body.items;
}

/**
 * Change a play that is already recorded (WI-039).
 *
 * Absent means leave alone; `null` clears. That distinction is the whole
 * point of a PATCH here -- most plays are logged with one tap and nothing
 * else, so the common edit adds a field rather than replacing a record.
 *
 * `review` and `durationMinutes` are accepted by the API and not yet offered
 * by any screen; they are in the type because leaving them out would make the
 * client look like the contract.
 */
export interface SessionPatch {
  playedAt?: string;
  rating?: number | null;
  review?: string | null;
  notes?: string | null;
  durationMinutes?: number | null;
}

/**
 * The shelf, not the session.
 *
 * A changed rating re-projects onto the shelf entry (ADR-0021), so that is
 * what the API hands back -- the caller already knows what it sent.
 */
const updatedSessionSchema = z.object({
  id: z.string(),
  shelf: shelfEntrySchema.omit({ slug: true }).nullable().optional(),
});
export type UpdatedSession = z.infer<typeof updatedSessionSchema>;

export function updateSession(id: string, patch: SessionPatch): Promise<UpdatedSession> {
  return request(`/me/sessions/${encodeURIComponent(id)}`, updatedSessionSchema, {
    method: "PATCH",
    body: patch,
    authenticated: true,
  });
}

export function deleteSession(id: string): Promise<void> {
  return requestNoContent(`/me/sessions/${encodeURIComponent(id)}`, {
    method: "DELETE",
    authenticated: true,
  });
}
